import { useDisplayedCarouselItem } from '@/hooks/use-displayed-carousel-item';
import { scrollIntoView } from '@/utils/scroll';
import { EnumButtonName } from '@calm/evexia-analytics';
import { useIntersectionObserver } from '@rhg/hooks/use-intersection-observer';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Theme, SxProps, ButtonBase } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ReactNode, RefObject, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

interface CarouselItemProps {
    position: number;
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    children?: ReactNode;
    nonVisibleCarouselItemSx?: SxProps<Theme>;
    trackItemScrollButton?: EnumButtonName;
}

const styleCarouselItem = {
    minHeight: 'min-content',
    maxWidth: 'min-content',
    transition: 'opacity 300ms ease-in-out',
    scrollSnapAlign: 'center',
    position: 'relative',
};

const styleUnclickableItemOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: (theme: Theme) => theme.zIndex.fab - 1,
};

const PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE = 0.8;

export const CarouselItem = ({
    children,
    position,
    nonVisibleCarouselItemSx,
    trackItemScrollButton,
    scrollContainerRef,
}: CarouselItemProps) => {
    const carouselItemRef = useRef<HTMLDivElement>(null);
    const [isItemClickable, setIsItemClickable] = useState(true);
    const displayedCarouselProgramContext = useDisplayedCarouselItem();
    const { formatMessage } = useIntl();

    const intersectingCallback = useCallback(
        (entry: IntersectionObserverEntry) => {
            const isIntersecting =
                entry.intersectionRatio >=
                PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE;
            setIsItemClickable(isIntersecting);
            if (isIntersecting) {
                displayedCarouselProgramContext?.addVisibleItem(position);
                displayedCarouselProgramContext?.setDisplayedItemIndex(
                    position
                );
            }
        },
        [displayedCarouselProgramContext, position]
    );

    const observeRef = useIntersectionObserver({
        intersectingCallback,
        nonIntersectingCallback: () => {
            setIsItemClickable(false);
            displayedCarouselProgramContext?.removeVisibleItem(position);
        },
        observerOptions: {
            rootMargin: '0px',
            threshold: PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE,
        },
    });

    const setCarouselItemRef = useCallback(
        (node: HTMLDivElement | null) => {
            carouselItemRef.current = node;
            observeRef(node);
        },
        [observeRef]
    );

    const handleScroll = () => {
        if (!scrollContainerRef.current || !carouselItemRef.current) return;
        scrollIntoView(scrollContainerRef.current, carouselItemRef.current, {
            onlyIfNotFullyVisible: true,
            callback: () =>
                // Needed to scroll the viewport up to the item if it is off-screen vertically
                carouselItemRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'center',
                }),
        });
    };

    if (isItemClickable) {
        return (
            <Box
                ref={setCarouselItemRef}
                sx={styleCarouselItem}
                role="listitem"
            >
                {children}
            </Box>
        );
    }

    return (
        <Box
            ref={setCarouselItemRef}
            sx={sxAppend(styleCarouselItem, nonVisibleCarouselItemSx)}
            role="listitem"
        >
            {children}
            <ButtonBase
                tabIndex={-1}
                trackButton={trackItemScrollButton ?? 'NotMapped'}
                sx={styleUnclickableItemOverlay}
                onClick={handleScroll}
                aria-label={formatMessage(
                    commonMessages['common.scroll_into_view']
                )}
            />
        </Box>
    );
};
